import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/apiClient";
import useEvalMode from "../lib/useEvalMode";

/**
 * PlatformContext — exposes site-wide platform flags to every page.
 *
 * Flags:
 *  - preview_mode             — soft-launch mode (buy/bid disabled).
 *  - preview_notice           — copy used by the sitewide preview banner.
 *  - public_auctions_enabled  — compliance toggle. When `false`, all
 *                               public-facing auction surfaces (nav links,
 *                               sections, CTAs, language, routes, cards) are
 *                               hidden from collectors and unauthenticated
 *                               visitors. Admin and breeder tools keep
 *                               working — the auction system is preserved.
 *
 * Fetched once on app mount, refreshable by the admin toggles. Defaults are
 * fail-safe: preview_mode defaults ON (so commerce CTAs never flash before
 * the real state arrives), and public_auctions_enabled defaults OFF for the
 * same reason — we never want bidding language to flash for a compliance
 * reviewer between a cold-load and the settings response.
 *
 * v3.99ba — Field Access ("public evaluator") override. When the current
 * browser session has activated eval mode via the private `/field-access/`
 * slug, we override `bank_approval_mode → false`, `preview_mode → false`,
 * `preview_notice → ""`, and `public_auctions_enabled → true` so the
 * evaluator sees the fully-launched product. Eval mode is per-tab and
 * never reaches the backend — the underlying admin settings stay
 * untouched.
 */
const PlatformContext = createContext({
  preview_mode: true,
  preview_notice: "",
  public_auctions_enabled: false,
  public_potency_enabled: false,
  bank_approval_mode: false,
  eval_mode: false,
  loaded: false,
  refresh: () => {},
});

export function PlatformProvider({ children }) {
  const [state, setState] = useState({
    preview_mode: true,
    preview_notice: "",
    public_auctions_enabled: false,
    public_potency_enabled: false,
    bank_approval_mode: false,
    loaded: false,
  });
  const { active: evalMode } = useEvalMode();

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/public/platform-settings");
      setState({ ...r.data, loaded: true });
    } catch {
      // On failure, keep preview_mode=true and public_auctions_enabled=false
      // so we fail safe (no auction surfaces flash).
      setState((s) => ({ ...s, loaded: true }));
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // P0-B (Feb 2026) — Hard production override.
  // The preview-access banner + "Notify me when buying opens" CTAs were
  // surfacing on the LIVE site (thecannaforge.com) because the admin
  // preview_mode flag was on. The live site is open for business — buyers
  // must NEVER see preview language regardless of what the admin flag
  // says. Detect by hostname (only the canonical production domain
  // qualifies) so preview/staging deployments still surface the banner.
  const isProductionHost = (() => {
    if (typeof window === "undefined") return false;
    const h = (window.location.hostname || "").toLowerCase();
    return h === "thecannaforge.com" || h === "www.thecannaforge.com";
  })();

  // v3.99ba — Apply Field Access overrides AFTER the real settings load so
  // the evaluator never sees a flash of BAM / preview UI before the
  // override kicks in. Eval mode is purely a presentation layer; it
  // never touches the backend.
  let effective;
  if (evalMode) {
    effective = {
      ...state,
      bank_approval_mode: false,
      preview_mode: false,
      preview_notice: "",
      public_auctions_enabled: true,
      public_potency_enabled: true,
      eval_mode: true,
    };
  } else if (isProductionHost) {
    // Production: force the soft-launch flags OFF and treat the page as
    // fully loaded so commerce CTAs render immediately. If /api/public/
    // platform-settings is slow or fails on prod, the page still renders
    // a live experience instead of stalling on the loading gate.
    effective = {
      ...state,
      preview_mode: false,
      preview_notice: "",
      bank_approval_mode: false,
      public_auctions_enabled: true,
      loaded: true,
      eval_mode: false,
    };
  } else {
    effective = { ...state, eval_mode: false };
  }

  return (
    <PlatformContext.Provider value={{ ...effective, refresh }}>
      {children}
    </PlatformContext.Provider>
  );
}

export const usePlatform = () => useContext(PlatformContext);
